import { reportEvent } from './util';

const storageKey = 'playerTimeObject';

/**
 * 性能监控配置中心
 * 支持多种开关控制方式，优先级：localStorage > URL参数 > 环境变量
 */
const PERF_CONFIG = {
  /**
   * 主开关控制逻辑
   * 1. localStorage 设置优先级最高（运行时可动态控制）
   * 2. URL 参数控制（方便测试和调试）
   * 3. 环境变量控制（开发环境默认开启，生产环境默认关闭）
   */
  enabled: (() => {
    // 优先检查 localStorage 设置
    const localSetting = window.localStorage?.getItem('perf_enabled');
    if (localSetting === 'true') return true;
    if (localSetting === 'false') return false;

    // 其次检查 URL 参数
    if (window.location?.search?.includes('perf=1')) return true;
    if (window.location?.search?.includes('perf=0')) return false;

    // 最后根据环境变量决定默认行为
    return process.env.NODE_ENV === 'development';
  })(),

  // 内存优化配置 - 限制存储数量防止内存溢出
  maxMetrics: 50,        // 最大时间点记录数量
  maxPhases: 30,         // 最大阶段记录数量

  // 性能优化配置
  persistDelay: 1000,    // 防抖持久化延迟时间（毫秒）
  autoCleanupMs: 300000, // 自动清理间隔（5分钟）
};

/**
 * 轻量级日志工具
 * 支持开发环境和调试模式的条件输出，具备良好的兼容性
 */
const logger = {
  // 判断是否应该输出日志
  isDev: PERF_CONFIG.enabled && (
    process.env.NODE_ENV === 'development'
    || (window.location && window.location.search && window.location.search.includes('debugger=1'))
    || (window.localStorage && window.localStorage.getItem('debugger') === '1')
  ),

  /**
   * 普通日志输出
   * @param {...any} args - 日志参数
   */
  log(...args) {
    if (this.isDev && console && console.log) {
      console.log('[linux][perf]', ...args);
    }
  },

  /**
   * 警告日志输出
   * @param {...any} args - 日志参数
   */
  warn(...args) {
    if (this.isDev && console && console.warn) {
      console.warn('[linux][perf]', ...args);
    }
  },

  /**
   * 错误日志输出
   * @param {...any} args - 日志参数
   */
  error(...args) {
    if (this.isDev && console && console.error) {
      console.error('[linux][perf]', ...args);
      reportEvent({
        name: 'linux-player-error-log',
        ext2: args,
      });
    }
  },
};

/**
 * 计算时间差（毫秒）
 * @param {number} startTime - 开始时间
 * @param {number} endTime - 结束时间
 * @returns {number} 时间差（毫秒，保留3位小数）
 */
const calculateDurationMs = (startTime, endTime) => Math.round((endTime - startTime) * 1000) / 1000;

/**
 * 性能监控类 - 低内存优化版本
 *
 * 主要特性：
 * 1. 快速禁用机制 - 未启用时创建最小化实例
 * 2. 内存优化 - 使用普通对象替代Map，限制存储数量
 * 3. 异步初始化 - 避免阻塞主线程
 * 4. 自动清理 - 定期清理过期数据
 * 5. 兼容性保障 - 全面的存在性检查
 */
class PerformanceTracker {
  constructor() {
    // 快速退出：如果未启用，创建最小化实例
    if (!PERF_CONFIG.enabled) {
      this.disabled = true;
      return;
    }

    // 初始化基础属性
    this.startTime = performance.now();
    this.sessionId = this.generateSessionId();

    // 使用普通对象替代Map，减少内存开销
    this.metrics = {};
    this.phases = {};
    this.metricsCount = 0;
    this.phasesCount = 0;

    // 兼容性检查 - 获取页面导航开始时间
    this.navigationStart = (performance && performance.timing && performance.timing.navigationStart)
      || Date.now();
    this.pageEnterTime = null;

    // 异步初始化，避免阻塞主线程
    this.initAsync();
  }

  /**
   * 异步初始化方法
   * 使用 setTimeout 将初始化操作推迟到下一个事件循环，避免阻塞主线程
   */
  async initAsync() {
    try {
      setTimeout(() => {
        this.initStorage();
        this.setupCleanup();
        this.recordTimePoint('navigation_start', this.navigationStart, 'absolute');
        this.recordTimePoint('performance_tracker_init', performance.now(), 'relative');
      }, 0);
    } catch (error) {
      logger.error('异步初始化失败:', error);
    }
  }

  /**
   * 设置自动清理机制
   * 定期清理过期数据，防止内存无限增长
   */
  setupCleanup() {
    if (typeof window !== 'undefined' && window.setTimeout) {
      this.cleanupTimer = setTimeout(() => {
        this.performCleanup();
      }, PERF_CONFIG.autoCleanupMs);
    }
  }

  /**
   * 执行数据清理
   * 保留最新的数据，清理过期记录，防止内存溢出
   */
  performCleanup() {
    if (this.disabled) return;

    try {
      // 清理 metrics - 保留最新记录
      if (this.metricsCount > PERF_CONFIG.maxMetrics) {
        const entries = Object.entries(this.metrics);
        entries.sort((a, b) => b[1].timestamp - a[1].timestamp);

        this.metrics = {};
        this.metricsCount = 0;

        // 只保留最新的条目
        entries.slice(0, PERF_CONFIG.maxMetrics).forEach(([key, value]) => {
          this.metrics[key] = value;
          this.metricsCount += 1;
        });
      }

      // 清理 phases - 保留最新记录
      if (this.phasesCount > PERF_CONFIG.maxPhases) {
        const entries = Object.entries(this.phases);
        entries.sort((a, b) => (b[1].endTime || b[1].startTime) - (a[1].endTime || a[1].startTime));

        this.phases = {};
        this.phasesCount = 0;

        entries.slice(0, PERF_CONFIG.maxPhases).forEach(([key, value]) => {
          this.phases[key] = value;
          this.phasesCount += 1;
        });
      }

      logger.log('清理完成', { metrics: this.metricsCount, phases: this.phasesCount });
    } catch (error) {
      logger.error('清理失败:', error);
    }
  }

  /**
   * 生成会话ID
   * 创建唯一的会话标识符，用于区分不同的性能监控会话
   * @returns {string} 格式: perf_时间戳_随机字符串
   */
  generateSessionId() {
    return `perf_${Date.now()}_${Math.random().toString(36)
      .substr(2, 5)}`;
  }

  /**
   * 初始化存储
   * 检查并清理可能存在的过期会话数据
   */
  initStorage() {
    if (this.disabled || typeof window === 'undefined' || !window.sessionStorage) return;

    try {
      const existingData = window.sessionStorage.getItem(storageKey);
      if (existingData) {
        const data = JSON.parse(existingData);
        // 如果会话ID不匹配，清理旧数据
        if (data.sessionId !== this.sessionId) {
          window.sessionStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      logger.error('存储初始化失败:', error);
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (e) {
        // 忽略清理错误，某些环境可能不支持 sessionStorage
      }
    }
  }

  /**
   * 记录时间点
   * 核心方法：记录性能监控的关键时间节点
   *
   * @param {string} key - 时间点标识符
   * @param {number|null} timestamp - 自定义时间戳，null 则使用当前时间
   * @param {string} type - 时间类型：'relative'(相对时间) | 'absolute'(绝对时间)
   * @param {Object} metadata - 附加元数据
   * @returns {Object|null} 记录对象或null（失败时）
   */
  recordTimePoint(key, timestamp = null, type = 'relative', metadata = {}) {
    if (this.disabled) return null;

    try {
      // 获取当前时间，兼容不同环境
      const now = timestamp || (performance && performance.now ? performance.now() : Date.now());
      const absoluteTime = type === 'absolute' ? now : this.navigationStart + now;
      const relativeTime = type === 'relative' ? now : now - this.navigationStart;

      // 创建最小化记录对象
      const record = {
        key,
        timestamp: absoluteTime,
        relativeTime,
        sessionId: this.sessionId,
        type,
      };

      // 只在有元数据时添加，减少内存占用
      if (metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0) {
        record.metadata = metadata;
      }

      // 存储到对象
      this.metrics[key] = record;
      this.metricsCount += 1;

      // 延迟持久化，避免频繁IO操作
      this.debouncedPersist();

      // 特殊处理：记录页面进入时间
      if (key === 'page_enter') {
        this.pageEnterTime = now;
      }

      // 开发环境输出详细日志
      if (logger.isDev) {
        const metaStr = record.metadata ? ` | ${JSON.stringify(record.metadata)}` : '';
        logger.log(`${key}: ${relativeTime.toFixed(1)}ms${metaStr}`);
      }

      return record;
    } catch (error) {
      logger.error('记录时间点失败:', error);
      return null;
    }
  }

  /**
   * 防抖持久化
   * 使用防抖机制减少频繁的存储操作，提升性能
   */
  debouncedPersist() {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistMetrics();
    }, PERF_CONFIG.persistDelay);
  }

  /**
   * 开始阶段计时
   * 开始监控某个阶段的性能，如组件挂载、API调用等
   *
   * @param {string} phaseKey - 阶段标识符
   * @param {Object} metadata - 阶段元数据
   * @returns {number|null} 开始时间或null（禁用时）
   */
  startPhase(phaseKey, metadata = {}) {
    if (this.disabled) return null;

    const startTime = performance && performance.now ? performance.now() : Date.now();

    // 创建阶段记录
    this.phases[phaseKey] = {
      startTime,
      status: 'running',
    };

    // 只在有元数据时添加
    if (metadata && Object.keys(metadata).length > 0) {
      this.phases[phaseKey].metadata = metadata;
    }

    this.phasesCount += 1;

    // 记录阶段开始时间点
    this.recordTimePoint(`${phaseKey}_start`, startTime, 'relative', {
      phase: phaseKey,
      action: 'start',
      ...metadata,
    });

    return startTime;
  }

  /**
   * 结束阶段计时
   * 结束某个阶段的性能监控，计算持续时间
   *
   * @param {string} phaseKey - 阶段标识符
   * @param {Object} metadata - 结束时的元数据
   * @returns {Object|null} 阶段结果对象或null
   */
  endPhase(phaseKey, metadata = {}) {
    if (this.disabled) return null;

    const endTime = performance && performance.now ? performance.now() : Date.now();
    const phase = this.phases[phaseKey];

    if (!phase) {
      logger.warn(`阶段 ${phaseKey} 未找到`);
      return null;
    }

    const duration = calculateDurationMs(phase.startTime, endTime);

    // 更新阶段信息
    phase.endTime = endTime;
    phase.duration = duration;
    phase.status = 'completed';

    if (metadata && Object.keys(metadata).length > 0) {
      phase.endMetadata = metadata;
    }

    // 记录阶段结束时间点
    this.recordTimePoint(`${phaseKey}_end`, endTime, 'relative', {
      phase: phaseKey,
      action: 'end',
      duration,
      ...metadata,
    });

    return {
      phaseKey,
      duration,
      startTime: phase.startTime,
      endTime,
      metadata: { ...(phase.metadata || {}), ...metadata },
    };
  }

  /**
   * 计算两个时间点之间的持续时间
   *
   * @param {string} startKey - 开始时间点标识符
   * @param {string} endKey - 结束时间点标识符
   * @returns {Object|null} 持续时间信息或null
   */
  calculateDuration(startKey, endKey) {
    if (this.disabled) return null;

    const start = this.metrics[startKey];
    const end = this.metrics[endKey];

    if (!start || !end) {
      logger.warn(`无法计算持续时间: ${startKey} 或 ${endKey} 未找到`);
      return null;
    }

    return {
      duration: calculateDurationMs(start.relativeTime, end.relativeTime),
      startTime: start.relativeTime,
      endTime: end.relativeTime,
      startKey,
      endKey,
    };
  }

  /**
   * 持久化指标数据
   * 将关键性能数据保存到 sessionStorage，用于跨页面恢复
   */
  persistMetrics() {
    if (this.disabled || typeof window === 'undefined' || !window.sessionStorage) return;

    try {
      // 只存储必要的元数据，减少存储空间占用
      const data = {
        sessionId: this.sessionId,
        startTime: this.startTime,
        metricsCount: this.metricsCount,
        phasesCount: this.phasesCount,
        lastUpdated: Date.now(),
      };

      window.sessionStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      logger.error('持久化失败:', error);
    }
  }

  /**
   * 生成性能报告
   * 创建包含所有性能指标的综合报告
   *
   * @param {boolean} includeRawData - 是否包含原始数据
   * @returns {Object} 性能报告对象
   */
  generateReport(includeRawData = false) {
    if (this.disabled) {
      return { disabled: true, message: '性能监控已禁用' };
    }

    const now = performance && performance.now ? performance.now() : Date.now();
    const totalDuration = calculateDurationMs(this.startTime, now);

    // 基础统计信息
    const stats = {
      sessionId: this.sessionId,
      totalDuration,
      metricsCount: this.metricsCount,
      phasesCount: this.phasesCount,
      generatedAt: new Date().toISOString(),
    };

    // 分析关键指标和阶段性能
    const keyMetrics = this.analyzeKeyMetrics();
    const phaseAnalysis = this.analyzePhases();

    const report = {
      stats,
      keyMetrics,
      phaseAnalysis,
    };

    // 可选包含原始数据
    if (includeRawData) {
      report.rawData = {
        metrics: this.metrics,
        phases: this.phases,
      };
    }

    return report;
  }

  /**
   * 分析关键性能指标
   * 分析预定义的关键时间点和时间间隔
   *
   * @returns {Object} 包含关键点和时间间隔的分析结果
   */
  analyzeKeyMetrics() {
    // 预定义的关键性能时间点
    const keyPoints = [
      'jump_start_time',      // 跳转开始时间
      'page_enter',           // 页面进入时间
      'component_mount_start', // 组件挂载开始时间
      'component_mount_end',   // 组件挂载结束时间
      'player_init_end',       // 播放器初始化结束时间
      'video_load_start',      // 视频加载开始时间
      'enter_cur_video_play',  // 进入当前视频播放时间
      'video_can_play',        // 视频可播放时间
      'video_playing',         // 视频播放时间
    ];

    const analysis = {};
    keyPoints.forEach((key) => {
      const metric = this.metrics[key];
      if (metric) {
        analysis[key] = {
          relativeTime: metric.relativeTime,
          timestamp: metric.timestamp,
          metadata: metric.metadata,
        };
      }
    });

    // 计算关键时间间隔
    const intervals = {
      jumpDuration: this.calculateDuration('jump_start_time', 'page_enter'),
      pageEnterDuration: this.calculateDuration('page_enter', 'component_mount_start'),
      videoLoadDuration: this.calculateDuration('video_load_start', 'enter_cur_video_play'),
      curPlayDuration: this.calculateDuration('page_enter', 'enter_cur_video_play'),
      timeToCanPlay: this.calculateDuration('enter_cur_video_play', 'video_can_play'),
      pageTimeToPlay: this.calculateDuration('page_enter', 'video_can_play'),
    };

    return { points: analysis, intervals };
  }

  /**
   * 分析阶段性能
   * 分析所有已完成阶段的性能数据
   *
   * @returns {Object} 阶段性能分析结果
   */
  analyzePhases() {
    const analysis = {};
    Object.keys(this.phases).forEach((key) => {
      const phase = this.phases[key];
      if (phase.status === 'completed') {
        analysis[key] = {
          duration: phase.duration,
          startTime: phase.startTime,
          endTime: phase.endTime,
          metadata: phase.metadata,
        };
      }
    });
    return analysis;
  }

  /**
   * 上传性能数据
   * 将性能报告上传到服务端进行分析
   *
   * @param {Object} options - 上传配置
   * @param {string} options.type - 上传类型：'incremental'|'full'|'final'
   * @param {string} options.reportType - 报告类型：'rumEvent' 等
   * @returns {boolean} 上传是否成功
   */
  uploadPerformanceData({ type = 'incremental', reportType = '' }) {
    if (this.disabled) return false;

    try {
      const report = this.generateReport(type === 'full');

      if (reportType === 'rumEvent') {
        const ext2Obj = {};
        const { intervals } = report.keyMetrics || {};

        // 提取时间间隔数据
        if (intervals) {
          Object.keys(intervals).forEach((key) => {
            const item = intervals[key];
            ext2Obj[key] = (item && item.duration !== null) ? item.duration : null;
          });
        }

        // 提取阶段分析数据
        const { phaseAnalysis } = report;
        if (phaseAnalysis) {
          Object.keys(phaseAnalysis).forEach((key) => {
            const item = phaseAnalysis[key];
            ext2Obj[key] = (item && item.duration !== null) ? item.duration : null;
          });
        }

        // 发送性能数据
        reportEvent({
          name: 'linux-player-performance',
          ext2: ext2Obj,
          ext3: {
            type,
            sessionId: this.sessionId,
            // 移除原始数据以减少传输大小
            report: JSON.stringify({
              ...report,
              rawData: undefined,
            }),
          },
        });

        // 上报后清理数据
        this.cleanup();
      }

      logger.log(`[性能上报] ${type} 报告已上传`);
      return true;
    } catch (error) {
      logger.error('上传失败:', error);
      return false;
    }
  }

  /**
   * 清理所有数据
   * 清理内存和存储中的所有性能监控数据
   */
  cleanup() {
    if (this.disabled) return;

    try {
      // 清理定时器
      if (this.cleanupTimer) {
        clearTimeout(this.cleanupTimer);
      }
      if (this.persistTimer) {
        clearTimeout(this.persistTimer);
      }

      // 清理存储
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem(storageKey);
      }

      // 清理内存数据
      this.metrics = {};
      this.phases = {};
      this.metricsCount = 0;
      this.phasesCount = 0;

      logger.log('[性能监控] 清理完成');
    } catch (error) {
      logger.error('清理失败:', error);
    }
  }

  /**
   * 动态启用性能监控
   */
  enable() {
    PERF_CONFIG.enabled = true;
    this.disabled = false;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('perf_enabled', 'true');
    }
    logger.log('[性能监控] 已启用');
  }

  /**
   * 动态禁用性能监控
   */
  disable() {
    PERF_CONFIG.enabled = false;
    this.disabled = true;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('perf_enabled', 'false');
    }
    this.cleanup();
    logger.log('[性能监控] 已禁用');
  }

  /**
   * 检查性能监控是否启用
   * @returns {boolean} 是否启用
   */
  isEnabled() {
    return PERF_CONFIG.enabled && !this.disabled;
  }
}

// 创建全局性能监控实例
const performanceTracker = new PerformanceTracker();

/**
 * 性能监控对外接口
 * 提供简化的API供外部调用，内部自动处理启用状态检查
 */
export const performanceReport = {
  // 基础时间记录
  recordTimePoint: (key, metadata) => performanceTracker.recordTimePoint(key, null, 'relative', metadata),
  recordAbsoluteTime: (key, timestamp, metadata) => performanceTracker.recordTimePoint(key, timestamp, 'absolute', metadata),

  // 阶段管理
  startPhase: (key, metadata) => performanceTracker.startPhase(key, metadata),
  endPhase: (key, metadata) => performanceTracker.endPhase(key, metadata),

  // 分析和报告
  calculateDuration: (startKey, endKey) => performanceTracker.calculateDuration(startKey, endKey),
  generateReport: includeRawData => performanceTracker.generateReport(includeRawData),

  // 数据管理
  upload: ({ type, reportType }) => performanceTracker.uploadPerformanceData({ type, reportType }),
  cleanup: () => performanceTracker.cleanup(),

  // 控制接口
  enable: () => performanceTracker.enable(),
  disable: () => performanceTracker.disable(),
  isEnabled: () => performanceTracker.isEnabled(),

  // 获取实例（用于高级操作）
  getInstance: () => performanceTracker,
};

/**
 * 事件监听器设置
 * 在页面关键时刻自动触发性能数据处理
 */
if (PERF_CONFIG.enabled && typeof window !== 'undefined') {
  /**
   * 页面卸载前处理
   * 在页面即将卸载时上传最终的性能数据
   */
  const handleBeforeUnload = () => {
    if (performanceTracker.isEnabled()) {
      performanceTracker.uploadPerformanceData({ type: 'final' });
    }
  };

  /**
   * 页面可见性变化处理
   * 记录页面隐藏/显示状态变化
   */
  const handleVisibilityChange = () => {
    if (performanceTracker.isEnabled()) {
      performanceTracker.recordTimePoint('page_visibility_change', null, 'relative', {
        hidden: document && document.hidden,
      });
    }
  };

  // 兼容性事件监听器注册
  if (window.addEventListener) {
    window.addEventListener('beforeunload', handleBeforeUnload);
  } else if (window.attachEvent) {
    // IE 兼容性
    window.attachEvent('onbeforeunload', handleBeforeUnload);
  }

  if (document && document.addEventListener) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
}

export default performanceReport;
